import { clearAllBodyScrollLocks, enableBodyScroll } from 'body-scroll-lock';
import { useEffect, useRef } from 'react';

import CloseIcon from '~/assets/icons/Close';
import useClickOutside from '~/hooks/useClickOutside';
import { getAppRoot } from '~/utils/dom';

import type {
  ModalCloseButtonMotion,
  ModalFrameBackground,
  ModalFrameProps
} from './types';

const BACKGROUND_CLASSES: Record<ModalFrameBackground, string> = {
  concrete: 'bg-concrete',
  transparent: 'bg-transparent',
  white: 'bg-white'
};

const CLOSE_MOTION_CLASSES: Record<ModalCloseButtonMotion, string> = {
  'spin-in': 'animate-close-spin-in',
  'spin-out': 'animate-close-spin-out'
};

function frameWidthClasses(isFullBody: boolean, isFullWidth?: boolean) {
  if (isFullBody) return 'h-full w-full max-w-full';
  if (isFullWidth) return 'w-full max-w-full';

  return 'w-auto max-md:w-[90%] max-md:max-w-[90%] min-[1200px]:max-w-[1160px]';
}

export default function ModalFrame({
  onCloseClick,
  children,
  closeButtonMotion,
  disableClickOutside,
  hideClose,
  isFullBody,
  background = 'white',
  isFullWidth
}: ModalFrameProps) {
  const ref = useRef(null);
  const scrollTargetElement = getAppRoot();
  useClickOutside(
    ref,
    (event?: MouseEvent) => onCloseClick(event, 'backdrop'),
    disableClickOutside
  );

  useEffect(() => {
    function handler({ keyCode }: { keyCode: number }) {
      if (keyCode === 27) {
        onCloseClick(undefined, 'escape_key');
      }
    }
    document.addEventListener('keyup', handler);

    return () => document.removeEventListener('keyup', handler);
  });

  function onCloseOverlay() {
    onCloseClick(undefined, 'close_button');
    clearAllBodyScrollLocks();
    if (scrollTargetElement) enableBodyScroll(scrollTargetElement);
  }

  return (
    <div
      className={`relative ${frameWidthClasses(isFullBody, isFullWidth)} ${BACKGROUND_CLASSES[background]}`}
      ref={ref}
    >
      <div className={isFullBody ? 'h-full' : undefined}>
        <div className="h-full">
          {!hideClose && (
            <div
              className={`absolute flex cursor-pointer justify-end ${
                closeButtonMotion
                  ? 'right-5 top-5 z-[1]'
                  : 'right-[10px] top-[10px]'
              }`}
            >
              <button
                type="button"
                aria-label="Close"
                onClick={onCloseOverlay}
                className={`tap-target flex size-6 items-center justify-center ${
                  closeButtonMotion
                    ? CLOSE_MOTION_CLASSES[closeButtonMotion]
                    : 'mr-[10px] mt-[10px]'
                }`}
              >
                <CloseIcon width="12" height="12" stroke="black" />
              </button>
            </div>
          )}
          <div className="h-full">{children}</div>
        </div>
      </div>
    </div>
  );
}
